/* ===========================================================
   Site data service — single seam between the UI and its data.

   The pages render from a small reactive store:
     • Initial paint uses the seed dataset (the Claude design), so the
       site is never blank and works offline / before the VPS responds.
     • loadSite() fetches the live website snapshot from the VPS
       (the catalogue ingest, web-released products only) and swaps it
       in. Any failure (offline, 404, bad shape) silently keeps the seed.

   The contract served by the VPS at GET {VITE_API_BASE_URL}/api/site:
     {
       "ranges":   Range[],      // web-top-category groups, order|portfolio
       "products": Product[]     // web-released products, catalogue-fed
     }
   Marketing copy (CONTENT) stays in code — it is editorial, managed via
   the Trac-IT "Website" CMS later, not the catalogue.
   =========================================================== */

import { useSyncExternalStore } from 'react';
import { CONTENT } from '@/data/content';
import type { SiteContent } from '@/data/content';
import {
  RANGES as SEED_RANGES,
  PRODUCTS as SEED_PRODUCTS,
  deriveFormat,
} from '@/data/catalogue';
import type { Range, RangeKind, Product } from '@/data/catalogue';

export type { Range, RangeKind, Product };
export type { SiteContent };
export { deriveFormat };

// ── Editorial content store ───────────────────────────────────────────────
// Code defaults (the Claude design) paint instantly; loadContent() swaps in the
// live copy edited via the Trac-IT "Website" CMS and served by the VPS at
// GET /api/site-content. Any failure keeps the defaults so the site never blanks.
let currentContent: SiteContent = CONTENT;
const contentListeners = new Set<() => void>();

function subscribeContent(listener: () => void): () => void {
  contentListeners.add(listener);
  return () => {
    contentListeners.delete(listener);
  };
}

function getContentSnapshot(): SiteContent {
  return currentContent;
}

/**
 * Reactive editorial content. Components re-render when the live copy swaps in.
 * Prefer this over the static `content` export so CMS edits appear live.
 */
export function useContent(): SiteContent {
  return useSyncExternalStore(subscribeContent, getContentSnapshot, getContentSnapshot);
}

/**
 * @deprecated Static snapshot — use {@link useContent} (or `useSite().content`)
 * so edits made in the Trac-IT Website CMS render without a reload. Retained for
 * non-reactive call sites.
 */
export const content = CONTENT;

export type SiteSource = 'seed' | 'live';

export interface SiteDataset {
  ranges: Range[];
  products: Product[];
  source: SiteSource;
}

const SEED: SiteDataset = {
  ranges: SEED_RANGES,
  products: SEED_PRODUCTS,
  source: 'seed',
};

// ── Reactive store (external store + useSyncExternalStore) ────────────────────

let current: SiteDataset = SEED;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): SiteDataset {
  return current;
}

function setDataset(next: SiteDataset): void {
  current = next;
  emit();
}

// ── Initial-load status store ────────────────────────────────────────────────
// Tracks whether the first loadSite() call has settled (resolved or failed).
// Deep-linkable pages (product/range) use this to show a loading state instead
// of redirecting to home before the live catalogue has had a chance to arrive.
type LoadStatus = 'loading' | 'settled';
let loadStatus: LoadStatus = 'loading';
const loadListeners = new Set<() => void>();

function subscribeLoad(listener: () => void): () => void {
  loadListeners.add(listener);
  return () => {
    loadListeners.delete(listener);
  };
}

function getLoadSnapshot(): LoadStatus {
  return loadStatus;
}

function settleLoad(): void {
  if (loadStatus === 'settled') return;
  loadStatus = 'settled';
  loadListeners.forEach((l) => l());
}

/**
 * True while the initial catalogue load is still in flight. Once the first
 * loadSite() settles (live data swapped in OR failure keeping the seed), this
 * becomes false permanently. In SSR/prerender there is no async load, so it
 * reports false (settled) via the server snapshot.
 */
export function useSiteLoading(): boolean {
  return useSyncExternalStore(subscribeLoad, getLoadSnapshot, () => 'settled') === 'loading';
}

// ── Pure selectors over a dataset ─────────────────────────────────────────────

/**
 * The "Innovation" range is NOT a nav range. On the live site it does not
 * appear in the "Available to Order" menu/tabs — its products are showcased on
 * the dedicated /innovation page instead. Identified by slug so the catalogue
 * can keep exporting it as an order-kind range.
 */
const INNOVATION_SLUG = 'innovation';
const isInnovation = (r: Range): boolean => r.kind === 'order' && r.slug === INNOVATION_SLUG;

// Nav ranges exclude the Innovation range (it has its own page).
const rangesOf = (d: SiteDataset, kind: RangeKind): Range[] =>
  d.ranges.filter((r) => r.kind === kind && !isInnovation(r));
const innovationProductsOf = (d: SiteDataset): Product[] => {
  const range = d.ranges.find(isInnovation);
  return range ? d.products.filter((p) => p.rangeKey === range.key) : [];
};
const rangeOf = (d: SiteDataset, kind: RangeKind, slug: string): Range | undefined =>
  d.ranges.find((r) => r.kind === kind && r.slug === slug);
const rangeByKeyOf = (d: SiteDataset, key: string): Range | undefined => d.ranges.find((r) => r.key === key);
const productsForRangeOf = (d: SiteDataset, key: string): Product[] => d.products.filter((p) => p.rangeKey === key);
const productByCodeOf = (d: SiteDataset, code: string): Product | undefined =>
  d.products.find((p) => p.code.toLowerCase() === code.toLowerCase());

export interface SiteApi {
  content: SiteContent;
  source: SiteSource;
  ranges: Range[];
  products: Product[];
  getRanges: (kind: RangeKind) => Range[];
  getRange: (kind: RangeKind, slug: string) => Range | undefined;
  getRangeByKey: (key: string) => Range | undefined;
  getProductsForRange: (key: string) => Product[];
  getProductByCode: (code: string) => Product | undefined;
  /** Products in the special "Innovation" range — shown on /innovation. */
  getInnovationProducts: () => Product[];
  deriveFormat: typeof deriveFormat;
}

/** Reactive accessor — components re-render when the live snapshot swaps in. */
export function useSite(): SiteApi {
  const d = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const c = useSyncExternalStore(subscribeContent, getContentSnapshot, getContentSnapshot);
  return {
    content: c,
    source: d.source,
    ranges: d.ranges,
    products: d.products,
    getRanges: (kind) => rangesOf(d, kind),
    getRange: (kind, slug) => rangeOf(d, kind, slug),
    getRangeByKey: (key) => rangeByKeyOf(d, key),
    getProductsForRange: (key) => productsForRangeOf(d, key),
    getProductByCode: (code) => productByCodeOf(d, code),
    getInnovationProducts: () => innovationProductsOf(d),
    deriveFormat,
  };
}

/**
 * Synchronously seed the store for prerendering (SSR). The prerender script
 * fetches the live catalogue once in Node and seeds it before renderToString,
 * so product/range pages render with real data (no async load). Marks the
 * initial load settled so deep-link guards don't show a loading state.
 */
export function seedSiteData(data: { ranges: Range[]; products: Product[] }): void {
  current = { ranges: data.ranges, products: data.products, source: 'live' };
  loadStatus = 'settled';
}

// ── Live load from the VPS catalogue snapshot ─────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
const SITE_ENDPOINT = `${API_BASE}/api/site`;

interface RawSiteResponse {
  ranges?: unknown;
  products?: unknown;
}

function isRange(x: unknown): x is Range {
  if (typeof x !== 'object' || x === null) return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r.key === 'string' &&
    typeof r.slug === 'string' &&
    typeof r.label === 'string' &&
    (r.kind === 'order' || r.kind === 'portfolio')
  );
}

function isProduct(x: unknown): x is Product {
  if (typeof x !== 'object' || x === null) return false;
  const p = x as Record<string, unknown>;
  return typeof p.code === 'string' && typeof p.name === 'string' && typeof p.rangeKey === 'string';
}

let loadStarted = false;

/**
 * Fetch the live website snapshot once. Safe to call on startup; any failure
 * (offline, 404, timeout, malformed) keeps the seed dataset silently so the
 * marketing site always renders.
 */
export async function loadSite(timeoutMs = 6000): Promise<SiteSource> {
  if (loadStarted) return current.source;
  loadStarted = true;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(SITE_ENDPOINT, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return current.source;
    const json = (await res.json()) as RawSiteResponse;

    const ranges = Array.isArray(json.ranges) ? json.ranges.filter(isRange) : [];
    const products = Array.isArray(json.products) ? json.products.filter(isProduct) : [];

    // Only swap in if we got a usable shape; otherwise keep the seed.
    if (ranges.length > 0 && products.length > 0) {
      setDataset({ ranges, products, source: 'live' });
    }
    return current.source;
  } catch {
    // Network error / abort / parse failure → keep seed.
    return current.source;
  } finally {
    clearTimeout(timer);
    settleLoad();
  }
}

// ── Live editorial content from the VPS (Trac-IT Website CMS) ─────────────────

const CONTENT_ENDPOINT = `${API_BASE}/api/site-content`;

/** Shallow shape guard — a usable content doc has at least the home section. */
function isSiteContent(x: unknown): x is SiteContent {
  if (typeof x !== 'object' || x === null) return false;
  const c = x as Record<string, unknown>;
  return typeof c.home === 'object' && c.home !== null;
}

let contentLoadStarted = false;

/**
 * Fetch the live editorial content once. The VPS serves the full document
 * (defaults + CMS edits), so we replace wholesale when it's valid; any failure
 * (offline, 404, empty {}, malformed) silently keeps the code defaults.
 */
export async function loadContent(timeoutMs = 6000): Promise<void> {
  if (contentLoadStarted) return;
  contentLoadStarted = true;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(CONTENT_ENDPOINT, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    const json = (await res.json()) as unknown;
    if (isSiteContent(json)) {
      currentContent = json;
      contentListeners.forEach((l) => l());
    }
  } catch {
    // keep defaults
  } finally {
    clearTimeout(timer);
  }
}
